import React, { useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { CheckIcon } from '@assets/icons';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useDeleteAccommodationPlaceV2Mutation } from '../Accommodations.graphql.generated';
import { useUpdateAccommodationsSortOrder } from '@apps/admin/common/hooks/useUpdateAccommodationsSortOrder';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useAdminAccommodationsTelemetry } from '../Accommodations.telemetry';
import { useAccommodationsContext } from '../context/useAccommodationsContext';
import { addError } from '@shared/utils/logger';
import type { AccommodationCard } from '../Accommodations.types';

type UseRemoveHotelReturn = Readonly<{
  /** Hotel pending user confirmation in the delete dialog, or `null` when dialog is closed */
  hotelToDelete: AccommodationCard | null;
  /** IDs optimistically removed while mutations are in-flight — supports concurrent deletions */
  deletedIds: ReadonlySet<string>;
  handleRemove: (hotel: AccommodationCard) => void;
  handleCloseDialog: () => void;
  handleConfirmRemove: () => void;
  /**
   * Delete a hotel without showing the confirm dialog — caller has already captured the user's intent.
   *
   * Pass `{ silent: true }` to suppress per-item toasts and rethrow on error. Used by bulk callers
   * that aggregate results and emit a single summary toast.
   */
  handleRemoveWithoutConfirm: (hotel: AccommodationCard, opts?: { silent?: boolean }) => Promise<void>;
}>;

export const useRemoveHotel = (): UseRemoveHotelReturn => {
  const { tq } = useTranslation('accommodationsAdmin');
  const { toast } = useToast();

  const telemetry = useAdminAccommodationsTelemetry();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { pageSlug, isSuperAdmin, accommodationsData } = useAccommodationsContext();

  const [hotelToDelete, setHotelToDelete] = useState<AccommodationCard | null>(null);
  // Mutable Set — intentional exception to track optimistically removed hotels with intentional mutation for performance
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  const [deleteAccommodationPlace] = useDeleteAccommodationPlaceV2Mutation();
  const { updateSortOrder } = useUpdateAccommodationsSortOrder(() => updateWebsitePreview());

  const handleRemove = useEventCallback((hotel: AccommodationCard) => {
    telemetry.deleteItemDialog(pageSlug, { itemType: hotel.type, isSuperAdmin: !!isSuperAdmin });
    setHotelToDelete(hotel);
  });

  const handleCloseDialog = useEventCallback(() => {
    if (hotelToDelete) {
      telemetry.deleteItemDialogDismiss(pageSlug, { itemType: hotelToDelete.type, isSuperAdmin: !!isSuperAdmin });
    }
    setHotelToDelete(null);
  });

  const performRemove = useEventCallback(async (pendingHotel: AccommodationCard, options: { reopenDialogOnError: boolean; silent?: boolean }) => {
    const rawPlace = accommodationsData?.accommodationPlacesV2?.find(i => i.id === pendingHotel.id);
    telemetry.deleteItemSubmit(pageSlug, { itemType: pendingHotel.type, isSuperAdmin: !!isSuperAdmin, createdByType: rawPlace?.createdByType ?? undefined });

    // Optimistically remove from UI — new Set reference required for React re-render
    setDeletedIds(prev => {
      const next = new Set(prev);
      next.add(pendingHotel.id);
      return next;
    });
    // Update sort order immediately to prevent stale order if user reorders during in-flight delete
    updateSortOrder({ accommodationsPlaceIds: [pendingHotel.id], action: 'remove' });

    try {
      // Fire mutation in background (no awaitRefetchQueries)
      await deleteAccommodationPlace({
        variables: { id: pendingHotel.id },
        refetchQueries: ['AccommodationHotels'],
      });
      if (!options.silent) toast(tq`Hotel removed successfully.`, { icon: <CheckIcon /> });
    } catch (error) {
      // Rollback on error — remove ID from deletedIds and (for the dialog flow) reopen dialog
      setDeletedIds(prev => {
        const next = new Set(prev);
        next.delete(pendingHotel.id);
        return next;
      });
      if (options.reopenDialogOnError) {
        setHotelToDelete(pendingHotel);
      }
      if (!options.silent) toast(tq`An error occurred while deleting the hotel. Please try again.`, { icon: <WarningTriangle /> });
      addError(error, { context: 'useRemoveHotel', message: 'Failed to remove hotel' });
      if (options.silent) throw error;
    }
  });

  const handleConfirmRemove = useEventCallback(async () => {
    if (!hotelToDelete) return;
    const pendingHotel = hotelToDelete;
    setHotelToDelete(null);
    await performRemove(pendingHotel, { reopenDialogOnError: true });
  });

  const handleRemoveWithoutConfirm = useEventCallback(async (hotel: AccommodationCard, opts?: { silent?: boolean }) => {
    await performRemove(hotel, { reopenDialogOnError: false, silent: opts?.silent });
  });

  return {
    hotelToDelete,
    deletedIds,
    handleRemove,
    handleCloseDialog,
    handleConfirmRemove,
    handleRemoveWithoutConfirm,
  };
};
